import { useMemo } from 'react';

import type { Asset2ReviewStatus, Maybe } from '../../../../../apis/graphql';
import type { CreativesBuilderValues } from '../../../../../common/AdForm/CreativeBuilders/types';
import type { PillColor } from '../../../../../common/Pill';
import { stringifyApprovedPublishers } from '../../../../../configs';
import { useCalculatedCreativeData } from '../../../../../hooks/useCalculatedCreativeData';
import type { Nullable, TargetingTermValue } from '../../../../../models';
import { stringifyTagList } from '../../../../../utils/formatting';
import { getAdCountryListForReview } from '../../../../../utils/reviewDerivation';
import { getTranscodePillColorForAsset2 } from '../helpers';

export type UseAssetIdsForTranscodeStatusReturn = {
  loading: boolean;
  review?: Asset2ReviewStatus;
  transcodePillColor: PillColor | null;
  transcodeStatus: string;
  assetTagList: string;
  allowedPublishersText: string;
  approvedCountries: string[] | null;
};

type Props = {
  creative: Nullable<CreativesBuilderValues>;
  publisherList: string[];
  lineItemCountries?: Maybe<Maybe<string>[]>;
  targetingTermValues?: TargetingTermValue[];
};

export function useAssetIdsForTranscodeStatus({
  creative,
  publisherList,
  lineItemCountries,
  targetingTermValues,
}: Props): UseAssetIdsForTranscodeStatusReturn {
  // Derived from the SAVED ad (props), not live Formik, so the sidenav only recalculates on save.
  const countryList = useMemo(() => getAdCountryListForReview(lineItemCountries, targetingTermValues ?? []), [
    lineItemCountries,
    targetingTermValues,
  ]);

  const { calculatedCreativeData, loading } = useCalculatedCreativeData({
    creative,
    publisherList,
    countryList,
  });

  const transcodeStatus = calculatedCreativeData?.transcodingStatus;
  const pillColor = transcodeStatus ? getTranscodePillColorForAsset2(transcodeStatus) : null;
  const approvedCountries = calculatedCreativeData?.approvedCountries;

  return {
    loading,
    review: calculatedCreativeData?.status,
    transcodePillColor: pillColor,
    transcodeStatus: transcodeStatus?.replace(/_/g, ' ') || '',
    assetTagList: stringifyTagList(calculatedCreativeData?.assetTagList),
    allowedPublishersText: stringifyApprovedPublishers(calculatedCreativeData?.allowedPublisherList || []),
    approvedCountries: approvedCountries?.length ? approvedCountries : null,
  };
}
